/**
 * [Insider+] Single gate + hub page for the whole /investor-hub tree.
 *
 * The URL is the source of truth for what's shown — no hidden state:
 *   /investor-hub            -> welcome hub (subscribed) or join flow (not)
 *   /investor-hub/<module>   -> that module (subscribed) or join flow (not,
 *                                same URL resolves to the module once
 *                                subscribed — no redirect needed)
 *
 * As the user navigates inside the child, it reports IFRAME_ROUTE_CHANGED
 * and we mirror that onto the visible parent URL via history.replace, so
 * the address bar always matches what's actually shown.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { INSIDER_PLUS_BASE_URL } from '../security/config';
import LoaderWrap from '../components/Loader/LoaderWrap';
import LoaderIcon from '../components/Loader/LoaderIcon';
import InsiderPlusIframeShell, {
  InsiderPlusPageWrapper,
} from '../components/InsiderPlus/InsiderPlusIframeShell';
import { selectHasInsiderPlusSubscription } from '../store/selectors/userSelectors';
import { INSIDER_PLUS_PAGE } from '../constants/pages';
import { INSIDER_PLUS_APP_PATHS } from '../constants/insiderPlusAppPaths';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import {
  INSIDER_PLUS_ENTRY_SOURCE,
  INSIDER_PLUS_RETURN_PATH,
} from '../constants/sessionStorage';
import {
  DISCOURSE_SOURCE,
  OPEN_CHECKOUT_PARAM,
} from '../util/helpers/insiderPlusEntryHelper';
import { isSafeInsiderPlusPath } from '../util/helpers/insiderPlusPathHelper';
import useInsiderPlusIframe from '../util/hooks/useInsiderPlusIframe';
import useGtmHook from '../util/hooks/useGtmHook';

const NPS_FIRED_KEY = 'nps_welcome_fired';

// Everything in the URL after /investor-hub is the deep module path into the
// child app (e.g. /investor-hub/metals-playbook -> /metals-playbook). Falls
// back to null (welcome hub) when there's no sub-path, or when the sub-path
// somehow fails the same safety check used for external deep links.
const getModulePathFromLocation = (pathname) => {
  const subPath = pathname.slice(INSIDER_PLUS_PAGE.length);
  // Treat both no sub-path and a bare trailing slash (/investor-hub/) as the
  // welcome hub — otherwise '/' passes isSafeInsiderPlusPath and loads the
  // child at its root instead of /welcome.
  if (!subPath || subPath === '/') return null;
  return isSafeInsiderPlusPath(subPath) ? subPath : null;
};

const InsiderPlusPage = () => {
  const hasInsiderPlusSubscription = useSelector(
    selectHasInsiderPlusSubscription,
  );
  const history = useHistory();
  const location = useLocation();
  const { gtmCustomEvent } = useGtmHook();

  // Set right before we call history.replace ourselves to mirror a child-
  // reported path — see useInsiderPlusIframe's skipNextLocationSyncRef doc.
  const skipNextLocationSyncRef = useRef(null);

  const buildIframeSrc = useCallback(() => {
    if (hasInsiderPlusSubscription !== true) {
      // Forward location.search here too (see the matching note below for
      // the subscriber branch) — e.g. a signup-flow query param the child
      // needs to auto-open its Join modal would otherwise be silently
      // dropped even though the /investor-hub redirect itself looks correct.
      return `${INSIDER_PLUS_BASE_URL}${INSIDER_PLUS_APP_PATHS.join}${location.search}`;
    }
    const modulePath = getModulePathFromLocation(location.pathname);
    // location.pathname never carries the query string (React Router keeps
    // it separately in location.search) — forward it explicitly, otherwise
    // e.g. /investor-hub/newsletters?tab=briefing silently drops ?tab=
    // before it ever reaches the child.
    return `${INSIDER_PLUS_BASE_URL}${
      modulePath ?? INSIDER_PLUS_APP_PATHS.welcome
    }${location.search}`;
  }, [hasInsiderPlusSubscription, location.pathname, location.search]);

  // Join flow: external CTAs open in a new tab, keeping the app open (as
  // before). Welcome/module flow: same-origin targets go through
  // react-router so the parent tree doesn't full-reload; external targets
  // (e.g. Discourse forum) fall back to window.location.href.
  const onNavigateUrl = useCallback(
    (url) => {
      if (hasInsiderPlusSubscription !== true) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      try {
        const target = new URL(url, window.location.origin);
        if (target.origin === window.location.origin) {
          history.push(target.pathname + target.search + target.hash);
          return;
        }
      } catch {
        // Malformed URL — fall through to full-page navigation
      }
      window.location.href = url;
    },
    [hasInsiderPlusSubscription, history],
  );

  // IFRAME_ROUTE_CHANGED: mirror the child's current path onto the visible
  // parent URL so the address bar always matches what's shown (no hidden
  // state). skipNextLocationSyncRef tells useInsiderPlusIframe the child is
  // already there — this history.replace is cosmetic, not a request to
  // reload the iframe.
  //
  // NPS_ELIGIBLE: the child emits this once per session when an active
  // subscriber lands on the welcome hub. Hotjar refuses to render surveys
  // inside an iframe, so we fire the NPS trigger here in the top-level window
  // via the parent's GTM/Hotjar. Guarded once per parent session.
  const onIframeMessage = useCallback(
    (event) => {
      if (event.data?.type === 'IFRAME_ROUTE_CHANGED') {
        // Only mirror child navigations to the parent URL for subscribers.
        // For a non-subscriber the join flow is shown at the original
        // /investor-hub/<module> deep link; mirroring the join child's
        // internal paths here would overwrite that deep link, so when the
        // subscription resolves getModulePathFromLocation would read the
        // clobbered path and load the wrong module (defeating "same URL
        // resolves to the module the moment they subscribe"). Same guard as
        // onNavigateUrl.
        if (hasInsiderPlusSubscription !== true) return;
        const path = event.data?.path;
        if (isSafeInsiderPlusPath(path)) {
          const target = `${INSIDER_PLUS_PAGE}${path}`;
          if (target !== location.pathname) {
            // history.replace splits `?` (query) and `#` (hash) off into
            // location.search / location.hash, so location.pathname afterward
            // won't include either even though `target` (and `path`) might —
            // match on the pathname-only portion so the hook's comparison
            // against location.pathname can actually succeed (otherwise the
            // skip is missed and the iframe needlessly reloads).
            const [targetPathname] = target.split(/[?#]/);
            skipNextLocationSyncRef.current = targetPathname;
            history.replace(target);
          }
        }
        return;
      }
      if (event.data?.type === 'NPS_ELIGIBLE') {
        if (sessionStorage.getItem(NPS_FIRED_KEY)) return;
        gtmCustomEvent('nps_welcome');
        sessionStorage.setItem(NPS_FIRED_KEY, '1');
      }
    },
    [gtmCustomEvent, history, location.pathname, hasInsiderPlusSubscription],
  );

  const {
    iframeRef,
    iframeSrc,
    iframeKey,
    isRecovering,
    authFailed,
    isRetrying,
    handleIframeLoad,
    handleRetry,
  } = useInsiderPlusIframe({
    buildIframeSrc,
    onNavigateUrl,
    onIframeMessage,
    skipNextLocationSyncRef,
  });

  // Subscribed users returning from Discourse land back at their original
  // /forum-auth entry so SSO completes, instead of whatever module the URL
  // currently points at.
  useEffect(() => {
    if (hasInsiderPlusSubscription !== true) return;
    const entrySource = retrieveFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE);
    const returnPath = retrieveFromSessionStorage(INSIDER_PLUS_RETURN_PATH);
    const isDiscourseReturn =
      entrySource === DISCOURSE_SOURCE &&
      typeof returnPath === 'string' &&
      returnPath.startsWith('/forum-auth');
    removeFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE);
    removeFromSessionStorage(INSIDER_PLUS_RETURN_PATH);
    if (isDiscourseReturn) {
      history.replace(returnPath);
    }
  }, [hasInsiderPlusSubscription, history]);

  // openCheckout=true only means anything for the non-subscriber join flow
  // (see buildIframeSrc above) — a subscriber landing here via the same
  // marketing entry has nothing to check out. Strip it so the address bar
  // doesn't keep showing a stale/meaningless param, and so it doesn't ride
  // along into the welcome/module iframe src for no reason.
  useEffect(() => {
    if (hasInsiderPlusSubscription !== true) return;
    if (!location.search.includes(OPEN_CHECKOUT_PARAM)) return;
    const params = new URLSearchParams(location.search);
    params.delete(OPEN_CHECKOUT_PARAM);
    const cleanSearch = params.toString();
    history.replace(
      `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`,
    );
  }, [hasInsiderPlusSubscription, location.pathname, location.search, history]);

  // Wait for subscription state to resolve before mounting any iframe, so
  // subscribers never flash the join page and non-subscribers never flash
  // welcome/module content.
  if (hasInsiderPlusSubscription === null) {
    return (
      <InsiderPlusPageWrapper>
        <LoaderWrap>
          <LoaderIcon />
        </LoaderWrap>
      </InsiderPlusPageWrapper>
    );
  }

  return (
    <InsiderPlusIframeShell
      iframeRef={iframeRef}
      iframeSrc={iframeSrc}
      iframeKey={iframeKey}
      title="Investor Hub"
      dataCy="container-insider-plus-page"
      isRecovering={isRecovering}
      authFailed={authFailed}
      isRetrying={isRetrying}
      onIframeLoad={handleIframeLoad}
      onRetry={handleRetry}
    />
  );
};

export default InsiderPlusPage;
